import { exportWidgetNodeSelector, exportWidgetStatusAttr } from 'common/constants/pdfExport';
import { ExportFooterData } from 'common/types/pdfExport';
import { Logger } from 'utilities';
import { requestExportFrame } from './exportFrames';

// Grace window for ExportWidgetRelay to announce via body attr after the target
// becomes ready. Pages with no notes/documents never set the attr.
const EXPORT_WIDGET_GRACE_MS = 800;
// Bounded wait for the relay to finish fetching; paused during 429 Retry-After.
const EXPORT_WIDGET_READY_TIMEOUT_MS = 10_000;

export const waitUntil = (
  win: Window,
  signal: AbortSignal,
  timeoutMs: number,
  predicate: () => boolean,
  isPaused?: () => boolean
): Promise<boolean> =>
  new Promise<boolean>(resolve => {
    let elapsed = 0;
    let last = win.performance.now();
    const tick = () => {
      if (signal.aborted) {
        resolve(false);
        return;
      }
      if (predicate()) {
        resolve(true);
        return;
      }
      const now = win.performance.now();
      if (!isPaused?.()) {
        elapsed += now - last;
      }
      last = now;
      if (elapsed >= timeoutMs) {
        resolve(false);
        return;
      }
      // Not `win.requestAnimationFrame`: rAF never fires while the tab is
      // hidden, which stalled the poll *and* froze the elapsed-time accounting
      // above, so neither timeout below could expire either.
      requestExportFrame(tick);
    };
    tick();
  });

/**
 * Reads the export-widget relay payload (client notes + document names) the
 * owning page published for this target. Returns undefined when the page has
 * no relay or the payload is empty/unreadable, so capture proceeds unchanged.
 */
export const collectExportWidgetData = async (
  iframe: HTMLIFrameElement,
  exportTarget: string,
  signal: AbortSignal
): Promise<ExportFooterData | undefined> => {
  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;
  if (!doc || !win) {
    return undefined;
  }
  const statusName = exportWidgetStatusAttr(exportTarget);
  const getStatus = () => doc.body.getAttribute(statusName);

  // A relay may mount a few frames after the target became ready; give it a
  // brief grace window to announce itself before concluding the page has none.
  const sawRelay = await waitUntil(win, signal, EXPORT_WIDGET_GRACE_MS, () => getStatus() != null);
  if (!sawRelay) {
    return undefined;
  }
  if (getStatus() !== 'ready') {
    await waitUntil(
      win,
      signal,
      EXPORT_WIDGET_READY_TIMEOUT_MS,
      () => getStatus() === 'ready',
      () => doc.body.hasAttribute('data-export-throttle-active')
    );
  }

  const node = doc.querySelector(exportWidgetNodeSelector(exportTarget));
  if (!node?.textContent) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(node.textContent) as ExportFooterData;
    const notes = Array.isArray(parsed?.notes) ? parsed.notes : [];
    const documents = Array.isArray(parsed?.documents) ? parsed.documents : [];
    if (!notes.length && !documents.length) {
      return undefined;
    }
    return { notes, documents };
  } catch (err) {
    Logger.warn(`pdfExport: failed to parse export-widget payload for ${exportTarget}`, err);
    return undefined;
  }
};
